import React from "react";
import { Tile, Badge } from "../Primitives";
import { BRANCHES } from "../../lib/data";
import { branchHealth, forecastDemand30, HEALTH_META } from "../../lib/dataPilot";
import { combineOrigins } from "../../lib/origin.jsx";
import { formatPeso, formatNumber, cn } from "../../lib/utils";

function useRows(valuation) {
  return BRANCHES.map(b => {
    const value = valuation.valueByBranch[b.id] ?? b.inventoryValue;
    return { ...b, value, excessValue: value * b.overstockRate, demand30: forecastDemand30(b, value), health: branchHealth(b) };
  });
}

function mixedOrigin(valuation, what) {
  return {
    origin: combineOrigins([valuation.origin, "synthetic"]),
    source: `${what}: synthetic sample dataset · Inventory value: ${valuation.source}`,
    updatedAt: valuation.asOf,
    parts: [
      { origin: valuation.origin === "mixed" ? "actual" : valuation.origin, text: "Inventory value (shared valuation)" },
      { origin: "synthetic", text: `${what} (sample dataset)` },
    ],
  };
}

export function StockoutRiskTile({ valuation, onOpen }) {
  const rows = useRows(valuation).sort((a, b) => b.stockoutRate - a.stockoutRate).slice(0, 8);
  return (
    <Tile className="col-span-12 lg:col-span-5" testid="stockout-risk-branches" title="Branches by Stockout Risk"
          origin={mixedOrigin(valuation, "Stockout rate & days on hand")}
          action={<Badge status="neutral">Stockout rate</Badge>}>
      <div className="divide-y divide-border -mx-4 -mt-4">
        {rows.map((b, i) => (
          <button key={b.id} data-testid={`risk-branch-${b.id}`} onClick={() => onOpen(b)}
                  className="w-full px-4 py-2.5 flex items-center gap-3 text-sm text-left hover:bg-surface-2 transition-colors">
            <span className="font-mono-tabular text-xs text-subtle w-6">#{i + 1}</span>
            <div className="flex-1 min-w-0">
              <div className="truncate font-medium">{b.name}</div>
              <div className="text-[11px] text-muted">{b.region} · {b.daysOnHand}d on hand · {formatPeso(b.value, { compact: true })}</div>
            </div>
            <div className="text-right">
              <div className="font-mono-tabular text-sm font-semibold text-critical">{(b.stockoutRate * 100).toFixed(1)}%</div>
              <div className="text-[10px] text-muted">stockout rate</div>
            </div>
          </button>
        ))}
      </div>
    </Tile>
  );
}

export function AtRiskBranchesTile({ valuation, onOpen }) {
  const rows = useRows(valuation)
    .filter(b => b.health !== "healthy")
    .sort((a, b) => (b.stockoutRate * 10 + b.excessValue / 1e7) - (a.stockoutRate * 10 + a.excessValue / 1e7))
    .slice(0, 8);
  const th = "py-2 px-3 border-b border-border";
  return (
    <Tile className="col-span-12" testid="at-risk-branches" title="At-Risk Branches · stock health"
          origin={mixedOrigin(valuation, "Stockout, days on hand, excess & 30-day demand")}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-muted">
              <th className={cn(th, "text-left")}>Branch</th>
              <th className={cn(th, "text-left")}>Region</th>
              <th className={cn(th, "text-left")}>Health</th>
              <th className={cn(th, "text-right")}>Stockout %</th>
              <th className={cn(th, "text-right")}>Days on hand</th>
              <th className={cn(th, "text-right")}>Inventory value</th>
              <th className={cn(th, "text-right")}>Excess value</th>
              <th className={cn(th, "text-right")}>Forecast demand · 30d</th>
            </tr>
          </thead>
          <tbody className="font-mono-tabular">
            {rows.map(b => (
              <tr key={b.id} data-testid={`at-risk-${b.id}`} onClick={() => onOpen(b)}
                  className="border-b border-border last:border-b-0 hover:bg-surface-2 cursor-pointer">
                <td className="py-2 px-3 font-sans">{b.name}</td>
                <td className="py-2 px-3 font-sans">{b.region}</td>
                <td className="py-2 px-3"><Badge status={HEALTH_META[b.health].tone}>{HEALTH_META[b.health].label}</Badge></td>
                <td className={cn("py-2 px-3 text-right", b.stockoutRate > 0.06 && "text-critical")}>{(b.stockoutRate * 100).toFixed(1)}%</td>
                <td className={cn("py-2 px-3 text-right", b.daysOnHand > 60 && "text-warning")}>{b.daysOnHand}</td>
                <td className="py-2 px-3 text-right">{formatPeso(b.value, { compact: true })}</td>
                <td className="py-2 px-3 text-right text-warning">{formatPeso(b.excessValue, { compact: true })}</td>
                <td className="py-2 px-3 text-right">{formatNumber(b.demand30)} u</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Tile>
  );
}
